import { FilterType, FilterTypeValue } from "@/components/filter/filter.types.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useDisqualifications } from "@/queries/recruit/use-disqualifications.query.ts";
import { useJobsByIds, useJobsSearch } from "@/queries/recruit/use-jobs.query.ts";
import {
  ApiJobsFilter,
  ApiJobsFilterDefault,
  getJobEmoji,
  JobStatus,
} from "@/types/recruit/job.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { FilterItem, Multiselect, SelectItem, Stack, Text } from "@skailer-com/uikit";
import { DateRangeFilter } from "@/components/date-filter/DateRangeFilter.tsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Control, Controller, FieldErrors, useController, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const useAppFilterRecruitRenders = ({
  control,
  errors,
  filters,
}: {
  control: Control;
  errors: FieldErrors;
  filters: FilterItem<FilterTypeValue>[];
}) => {
  const { t } = useTranslation();
  const { canIView } = usePermissions();
  const recruiterIds = useWatch({
    control,
    name: "recruiter_ids",
  }) as number[] | undefined;
  const selectedJobIds = useWatch({
    control,
    name: "job_ids",
  }) as number[] | undefined;
  const { field: jobsField } = useController({
    control,
    name: "job_ids",
  });
  const hasSelectedRecruiters = (recruiterIds?.length ?? 0) > 0;
  const prevRecruiterIdsRef = useRef<number[] | undefined>(recruiterIds);

  const defaultFilter = {
    ...ApiJobsFilterDefault,
    page_size: 50,
    recruiter_ids: recruiterIds || [],
    status_list: [JobStatus.published],
    my_jobs: false,
  };
  const [jobsFilter, setJobsFilter] = useState<ApiJobsFilter>(defaultFilter);
  const [syncedRecruiterIds, setSyncedRecruiterIds] = useState(recruiterIds);
  if (recruiterIds !== syncedRecruiterIds) {
    setSyncedRecruiterIds(recruiterIds);
    setJobsFilter((prev) => ({
      ...prev,
      recruiter_ids: recruiterIds || [],
    }));
  }

  const canViewJobs =
    filters.some((item) => item.type === FilterType.jobs) &&
    canIView(ApiPermission.recruiting_module);

  const canViewDisqualificationReasons =
    filters.some((item) => item.type === FilterType.disqualificationReason) &&
    canIView(ApiPermission.recruiting_module);

  const { data: jobsSearched, isLoading: jobsIsLoading } = useJobsSearch(jobsFilter, canViewJobs);
  const { data: jobsSelected } = useJobsByIds({
    ids: selectedJobIds || [],
    enabled: canViewJobs,
  });

  const jobSearchOptions = useMemo(() => {
    if (!jobsSearched) return [];
    return jobsSearched.items.map((job) => ({
      id: job.id,
      title: job.title,
      emoji: getJobEmoji(job),
      caption: job.department?.title,
    })) as SelectItem[];
  }, [jobsSearched]);

  const jobsSelectedOptions = useMemo(() => {
    if (!jobsSelected) return [];
    return jobsSelected.map((job) => ({
      id: job.id,
      title: job.title,
      emoji: getJobEmoji(job),
      caption: job.department?.title,
    })) as SelectItem[];
  }, [jobsSelected]);

  useEffect(() => {
    const prevRecruiterIds = prevRecruiterIdsRef.current || [];
    const nextRecruiterIds = recruiterIds || [];

    if (prevRecruiterIds.length > 0 && nextRecruiterIds.length === 0) {
      jobsField.onChange([]);
    }

    prevRecruiterIdsRef.current = recruiterIds;
  }, [jobsField, recruiterIds]);

  async function handleSearchJobs(text: string | undefined) {
    if (text) {
      setJobsFilter((prev) => ({
        ...prev,
        text: text,
        status_list: [JobStatus.published, JobStatus.archived, JobStatus.on_hold],
        my_jobs: false,
      }));
    } else {
      setJobsFilter({
        ...defaultFilter,
      });
    }
  }

  const renderFilterJobs = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewJobs) return null;

    return (
      <Controller
        key={item.name}
        render={({ field: { value, onChange } }) => (
          <Stack gap="xs">
            <Multiselect
              label={t("ats:job")}
              emptyTitle={t("common:select")}
              isLoading={jobsIsLoading}
              values={value}
              searchable
              options={jobsSelectedOptions}
              showAvatar={true}
              showCaption={true}
              onSearchOptions={handleSearchJobs}
              searchOptions={jobSearchOptions}
              onChange={(value) => {
                onChange(value);
              }}
              error={errors[item.name]?.message as string}
            />
            {hasSelectedRecruiters && (
              <Text className={"text-secondary text-sm"}>
                {t("ats:jobs_filtered_by_selected_recruiters")}
              </Text>
            )}
          </Stack>
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const disqualificationReasons = useDisqualifications(canViewDisqualificationReasons);

  const renderFilterDisqualificationReasons = (item: FilterItem<FilterTypeValue>) => {
    if (!canViewDisqualificationReasons) return null;

    return (
      <Controller
        key={item.name}
        render={({ field: { value, onChange } }) => (
          <Multiselect
            label={t("ats:disqualification_reason")}
            emptyTitle={t("ats:choose_disqualification_reason")}
            isLoading={disqualificationReasons.isLoading}
            values={value}
            searchable={true}
            options={disqualificationReasons.data}
            onChange={(value) => {
              onChange(value);
            }}
            error={errors[item.name]?.message as string}
          />
        )}
        control={control}
        name={item.name}
      />
    );
  };

  const renderFilterJobCreationDate = () => {
    return (
      <Controller
        key="job_creation_date"
        control={control}
        name="date_creation_from"
        render={({ field: fromField }) => (
          <Controller
            control={control}
            name="date_creation_to"
            render={({ field: toField }) => (
              <DateRangeFilter
                title={t("ats:creation_date")}
                from={fromField.value ?? undefined}
                to={toField.value ?? undefined}
                onChange={({ from, to }) => {
                  fromField.onChange(from ?? null);
                  toField.onChange(to ?? null);
                }}
              />
            )}
          />
        )}
      />
    );
  };

  return {
    renderFilterJobs,
    renderFilterDisqualificationReasons,
    renderFilterJobCreationDate,
  };
};
